// src/routes/Routes.jsx
import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";

import PatientCareAnalytics from "./pages/patient-care-analytics";
import NovusOperationsHub from "./pages/novus-operations-hub";
import VendorPerformanceHub from "./pages/vendor-performance-hub";
import PatientManagement from "./pages/patient-management";
import PackagesManagement from "./pages/packages-management";
import OrdersManagement from "./pages/orders-management";
import PushNotifications from "./pages/push-notifications";
import MasterManagement from "./pages/master-management";
import UserManagement from "./pages/user-management";
import CenterManagement from "./pages/center-management";
import ResetPassword from "./pages/reset-password/index";
import Prescription from "./pages/prescription";

import LoginPage from "pages/login";
import Banners from "pages/Banners";
import RegisterPage from "pages/register";
import ForgotPasswordPage from "pages/forgot-password-page";
import PatientDetails from "./pages/patient-management/components/PatientDetails";
import OrderDetails from "./pages/orders-management/components/Orderdetails";
import Payments from "./pages/payments";

import ProtectedRoute from "components/ProtectedRoute";
import VendorDetails from "./pages/vendor-performance-hub/components/VendorDetails";
import NewOrder from "./pages/orders-management/components/NewOrder";
import EditOrder from "./pages/orders-management/components/EditOrder";
import Bills from "./pages/orders-management/components/Bills";

import TestParameterSelector from "./pages/packages-management/components/TestParameterSelector";
import NewPatientPage from "./pages/orders-management/components/NewPatientPage";
import CouponItem from "./pages/coupons";
import Reports from "./pages/reports";
import CartsPage from "./pages/cart-activity";
import ExpiringOrders from "./pages/orders-management/components/ExpiringOrders";
import ProfilePage from "./pages/setting";
import LabOrders from "./pages/lab-orders";
import OnlineOrders from "./pages/online-orders";
import Centers from "./pages/center-management";
import Enquiry from "./pages/enquiry";
import PaymentSuccess from "./pages/PaymentSuccess";
import CollectionPriceManagement from "./pages/master-management/components/CollectionPriceManagement";
import DueReportsDashboard from "./pages/novus-operations-hub/components/DueReportsDashboard";
import ReportsPage from "./pages/reports/components/Reportspage";
import RefCentersPage from "./pages/statistics/RefCentersPage";
import DoctorsPage from "./pages/statistics/DoctorsPage";

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />

        <RouterRoutes>
          {/* AUTH */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password-page" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* PROTECTED */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute access="dashboard">
                <NovusOperationsHub />
              </ProtectedRoute>
            }
          />

          <Route
            path="/collection-prices"
            element={
              <ProtectedRoute access="master">
                <CollectionPriceManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/patient-care-analytics"
            element={
              <ProtectedRoute access="patient">
                <PatientCareAnalytics />
              </ProtectedRoute>
            }
          />

          <Route
            path="/banners"
            element={
              <ProtectedRoute access="banners">
                <Banners />
              </ProtectedRoute>
            }
          />

          <Route
            path="/coupons"
            element={
              <ProtectedRoute access="coupons">
                <CouponItem />
              </ProtectedRoute>
            }
          />

          <Route
            path="/enquiry"
            element={
              <ProtectedRoute access="enquiry">
                <Enquiry />
              </ProtectedRoute>
            }
          />

          <Route
            path="/centers"
            element={
              <ProtectedRoute access="centers">
                <Centers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cart-details"
            element={
              <ProtectedRoute access="cart">
                <CartsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute access="labReport">
                <Reports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/online-orders"
            element={
              <ProtectedRoute access="onlineOrders">
                <OnlineOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/lab-orders"
            element={
              <ProtectedRoute access="labOrders">
                <LabOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports-page"
            element={
              <ProtectedRoute access="statistics">
                <ReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendor-performance-hub"
            element={
              <ProtectedRoute access="vendor">
                <VendorPerformanceHub />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendors/:id"
            element={
              <ProtectedRoute access="vendor">
                <VendorDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/patient-management"
            element={
              <ProtectedRoute access="patient">
                <PatientManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:id"
            element={
              <ProtectedRoute access="patient">
                <PatientDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/new"
            element={
              <ProtectedRoute access="orders">
                <NewPatientPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/packages-management"
            element={
              <ProtectedRoute access="packages">
                <PackagesManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders-management"
            element={
              <ProtectedRoute access="orders">
                <OrdersManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/expire-orders"
            element={
              <ProtectedRoute access="orders">
                <ExpiringOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/new"
            element={
              <ProtectedRoute access="orders">
                <NewOrder />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/:id/edit"
            element={
              <ProtectedRoute access="orders">
                <EditOrder />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute access="orders">
                <OrderDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/bill/:orderId"
            element={
              <ProtectedRoute access="orders">
                <Bills />
              </ProtectedRoute>
            }
          />

          <Route
            path="/test-parameters/:testId"
            element={
              <ProtectedRoute access="orders">
                <TestParameterSelector />
              </ProtectedRoute>
            }
          />

          <Route
            path="/push-notifications"
            element={
              <ProtectedRoute access="notification">
                <PushNotifications />
              </ProtectedRoute>
            }
          />

          <Route
            path="/master-management"
            element={
              <ProtectedRoute access="master">
                <MasterManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user-management"
            element={
              <ProtectedRoute access="user">
                <UserManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute access="user">
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/center-management"
            element={
              <ProtectedRoute access="master">
                <CenterManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payments"
            element={
              <ProtectedRoute access="payments">
                <Payments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/prescriptions"
            element={
              <ProtectedRoute access="prescriptions">
                <Prescription />
              </ProtectedRoute>
            }
          />

          {/* ✅ FIX: TAT should be reports (or create separate "tat" right if you want) */}
          <Route
            path="/TAT"
            element={
              <ProtectedRoute access="statistics">
                <DueReportsDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/statistics/ref-centers"
            element={
              <ProtectedRoute access="statistics">
                <RefCentersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/statistics/ref-doctors"
            element={
              <ProtectedRoute access="statistics">
                <DoctorsPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;